import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  skipToken,
  useIsMutating,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import QRCode from "qrcode";
import { Smartphone, Copy, Check } from "lucide-react";
import { Dialog, useAnyDialogOpen } from "@/components/ui/Dialog";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  clearDismissedConfirmation,
  dismissedConfirmationNoticeId,
  readDismissedConfirmation,
  passkeysKeys,
  passkeysMutationKeys,
  setRecoveryCodesAtRisk,
  useDismissedConfirmation,
  usePasskeys,
  usePublishDismissedConfirmation,
  useRecoveryCodesAtRisk,
} from "../hooks/usePasskeys";
import type { PasskeyListResponse } from "../api/passkeys.api";
import {
  clearPendingTotpRecoveryCodes,
  consumeProvenTotpStepUpRejections,
  hasLatestProvenTotpStepUpRejection,
  isTotpActionMutationKey,
  totpMutationKeys,
  totpReportedStepUpRejectionIdsKey,
  useDeleteTotp,
  usePendingTotpRecoveryCodes,
  useStartTotpSetup,
  useVerifyTotpSetup,
} from "../hooks/useTotp";
import { ApiError, isSessionEndedError, isStaleSessionError } from "@/lib/api/client";
import { useStepUp } from "../hooks/useStepUp";
import { useCeremonyOwner } from "@/hooks/useCeremonyOwner";
import { useNetworkOnline } from "@/hooks/useNetworkOnline";
import { useMfaRecoveryHold } from "../mfa-recovery-hold";
import { focusMainTransiently } from "../focus-main";
import { StepUpDialog } from "./StepUpDialog";
import { StaleSessionNotice } from "./StaleSessionNotice";
import { FeatureCheckFailedCard } from "./FeatureCheckFailedCard";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { totpConfirmationReplacesCodes } from "../recovery-code-replacement";
import { ackRecoveryCodesShown } from "../recovery-codes-generation";

export function TotpSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, error, refetch, isFetching, isPaused } = usePasskeys();
  const online = useNetworkOnline();
  const recoveryCodesAtRisk = useRecoveryCodesAtRisk();
  const setup = useStartTotpSetup();
  const verify = useVerifyTotpSetup();
  const remove = useDeleteTotp();
  const ceremonyOwner = useCeremonyOwner();
  const stepUp = useStepUp(ceremonyOwner);
  const dismissedConfirmation = useDismissedConfirmation();
  const actionMutationTick = useMutationState({
    filters: {
      predicate: (mutation) => isTotpActionMutationKey(mutation.options.mutationKey),
    },
    select: (mutation) => `${mutation.mutationId}:${mutation.state.status}`,
  });
  void actionMutationTick;
  const { data: reportedTotpStepUpRejectionIds = [] } = useQuery({
    queryKey: totpReportedStepUpRejectionIdsKey,
    queryFn: skipToken,
    initialData: [] as number[],
    staleTime: Infinity,
    gcTime: Infinity,
  });
  void reportedTotpStepUpRejectionIds;
  const cacheProvenRejection = hasLatestProvenTotpStepUpRejection(queryClient);
  usePublishDismissedConfirmation(
    "totp",
    stepUp.dismissedRequired || (cacheProvenRejection && !stepUp.open),
    stepUp.acknowledgeDismissed,
  );
  const anyDialogOpen = useAnyDialogOpen();
  const passkeyPrepareOnWire = useIsMutating({ mutationKey: passkeysMutationKeys.prepare }) > 0;
  const passkeyEnrollOnWire = useIsMutating({ mutationKey: passkeysMutationKeys.enroll }) > 0;
  const passkeyRemoveOnWire = useIsMutating({ mutationKey: passkeysMutationKeys.remove }) > 0;
  const passkeyRegenerateOnWire =
    useIsMutating({ mutationKey: passkeysMutationKeys.regenerate }) > 0;
  const otherPendingId = useId();
  const unavailableId = useId();
  const pausedId = useId();
  const removePausedId = useId();

  const [setupOpen, setSetupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteOverdue, setDeleteOverdue] = useState(false);
  const [code, setCode] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [shownCodes, setShownCodes] = useState<string[] | null>(null);
  const pendingCodes = usePendingTotpRecoveryCodes();
  const visibleCodes = shownCodes ?? pendingCodes;
  const [copied, setCopied] = useState(false);
  const [checkRetryFailed, setCheckRetryFailed] = useState(false);
  const [checkRetrying, setCheckRetrying] = useState(false);
  const [checkFailedShown, setCheckFailedShown] = useState(false);
  const [dismissedNoticeShown, setDismissedNoticeShown] = useState(false);
  const [rejectionNoticeLatched, setRejectionNoticeLatched] = useState(false);
  const [removeCarriesEarlier, setRemoveCarriesEarlier] = useState(false);
  const [setupCarriesEarlier, setSetupCarriesEarlier] = useState(false);
  const [removal, setRemoval] = useState<"settled" | "landed" | null>(null);
  const reportHold = useMfaRecoveryHold("totp");
  const totpDialogs = useRef({ setup: false, codes: false });
  const mountedRef = useRef(true);
  const layoutShownRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const restoreHeadingRef = useRef(false);
  const emitHold = () => {
    if (!mountedRef.current) return;
    reportHold(totpDialogs.current.setup || totpDialogs.current.codes);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      reportHold(false);
    };
  }, [reportHold]);

  useEffect(() => {
    return () => {
      if (readDismissedConfirmation(queryClient) === "totp") {
        clearDismissedConfirmation(queryClient);
      }
    };
  }, [queryClient]);

  useLayoutEffect(() => {
    if (visibleCodes === null) return;
    const title = t("account.passkeys.recoveryCodesTitle");
    const codesDialogOpen = [...document.querySelectorAll("dialog[open]")].some(
      (dialog) => dialog.querySelector("h2")?.textContent === title,
    );
    if (!codesDialogOpen) return;
    totpDialogs.current.codes = true;
    setRecoveryCodesAtRisk(queryClient, false);
    reportHold(true);
    void ackRecoveryCodesShown(queryClient).then(() => {
      void queryClient.invalidateQueries({ queryKey: passkeysKeys.list() });
    });
  }, [visibleCodes, queryClient, reportHold, t]);

  const totpEnabled = data?.totpEnabled ?? false;
  const hasPasskey = (data?.passkeys?.length ?? 0) > 0;
  const passkeyImpliedByServer = stepUp.serverDemandedProof && !totpEnabled;
  const mfaEnabled = data?.mfaEnabled ?? false;
  const listUnchecked = data === undefined && (error !== null || isPaused);
  const listSessionEnded = listUnchecked && isSessionEndedError(error);
  const waitingForNetwork = data === undefined && !online;
  if (removal === "settled" && !totpEnabled && !anyDialogOpen) {
    setRemoval("landed");
  }
  const removeBusy = remove.isPending || (removal === "settled" && isFetching);
  const setupUnavailable = setup.error?.status === 503;
  const setupStarting = useIsMutating({ mutationKey: totpMutationKeys.start }) > 0;
  const setupPaused = useMutationState({
    filters: { mutationKey: totpMutationKeys.start, status: "pending" },
    select: (mutation) => mutation.state.isPaused,
  }).some(Boolean);
  const removePaused = useMutationState({
    filters: { mutationKey: totpMutationKeys.remove, status: "pending" },
    select: (mutation) => mutation.state.isPaused,
  }).some(Boolean);
  const setupLocked = setupStarting || setupUnavailable;
  const setupUnanswered = setup.error !== null && !(setup.error instanceof ApiError);
  const removeUnanswered = remove.error instanceof TypeError;

  const otpauthUri = setup.data?.otpauthUri ?? null;

  useEffect(() => {
    let cancelled = false;
    if (!otpauthUri) {
      return;
    }
    QRCode.toDataURL(otpauthUri, { margin: 1, width: 200 })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  const workOnScreen =
    anyDialogOpen ||
    setup.isPending ||
    verify.isPending ||
    remove.isPending ||
    visibleCodes !== null ||
    stepUp.open;
  const wantCheckFailed = listUnchecked || (checkFailedShown && data === undefined);
  if (!workOnScreen && checkFailedShown !== wantCheckFailed) {
    setCheckFailedShown(wantCheckFailed);
  }
  const showCheckFailed = workOnScreen ? checkFailedShown : wantCheckFailed;
  const carrierOwnsRetry = recoveryCodesAtRisk && listUnchecked;
  if (checkRetryFailed && (!showCheckFailed || carrierOwnsRetry || waitingForNetwork)) {
    setCheckRetryFailed(false);
  }
  const rejectionOutstanding =
    stepUp.dismissedRequired ||
    cacheProvenRejection ||
    (rejectionNoticeLatched && dismissedConfirmation === "totp");
  const showDismissedNotice =
    rejectionOutstanding && !setupOpen && (dismissedNoticeShown || !anyDialogOpen);
  const namedDismissal = dismissedConfirmation === "totp";
  const showCachedNotice = namedDismissal && !showDismissedNotice && !setupOpen && !anyDialogOpen;
  const otherPendingLabel =
    passkeyPrepareOnWire || passkeyEnrollOnWire
      ? t("account.passkeys.enrollWorking")
      : passkeyRemoveOnWire
        ? t("account.passkeys.deleting")
        : passkeyRegenerateOnWire
          ? t("account.passkeys.regenerating")
          : null;
  const otherPending = otherPendingLabel !== null;
  const setupShowsCarried = setupCarriesEarlier || dismissedConfirmation === "passkeys";
  if (dismissedNoticeShown !== showDismissedNotice) {
    setDismissedNoticeShown(showDismissedNotice);
  }
  const noticePainted = showDismissedNotice && !showCheckFailed;
  if (noticePainted && !rejectionNoticeLatched) {
    setRejectionNoticeLatched(true);
  }
  useEffect(() => {
    if (!rejectionNoticeLatched) return;
    consumeProvenTotpStepUpRejections(queryClient);
  }, [rejectionNoticeLatched, queryClient]);
  useEffect(() => {
    if (!showCheckFailed) {
      layoutShownRef.current = true;
      return;
    }
    const active = document.activeElement;
    const strandedInClosedDialog =
      active instanceof HTMLElement && active.closest("dialog:not([open])") !== null;
    const focusWasLost = active === null || active === document.body || strandedInClosedDialog;
    if (layoutShownRef.current && focusWasLost) {
      const moved = focusMainTransiently();
      if (!moved && strandedInClosedDialog && active instanceof HTMLElement) active.blur();
    }
    layoutShownRef.current = false;
  }, [showCheckFailed]);
  useLayoutEffect(() => {
    if (showCheckFailed || !restoreHeadingRef.current) return;
    restoreHeadingRef.current = false;
    headingRef.current?.focus();
    if (document.activeElement !== headingRef.current) focusMainTransiently();
  }, [showCheckFailed]);

  const startSetup = () => {
    if (setupLocked || queryClient.isMutating({ mutationKey: totpMutationKeys.start }) > 0) return;
    setCode("");
    setRemoval(null);
    if (!verify.isPending) verify.reset();
    totpDialogs.current.setup = true;
    emitHold();
    const open = () => {
      if (!mountedRef.current) return;
      totpDialogs.current.setup = true;
      if (readDismissedConfirmation(queryClient) === "passkeys") {
        setSetupCarriesEarlier(true);
      }
      setSetupOpen(true);
      emitHold();
    };
    if (setup.data) {
      open();
      return;
    }
    setup.mutate(undefined, {
      onSuccess: open,
      onError: () => {
        totpDialogs.current.setup = false;
        emitHold();
      },
    });
  };

  const closeSetup = () => {
    totpDialogs.current.setup = false;
    setSetupOpen(false);
    setSetupCarriesEarlier(false);
    if (!verify.isPending) verify.reset();
    stepUp.cancel();
    setCode("");
    emitHold();
  };

  const finishSetup = () => {
    closeSetup();
    setup.reset();
    setQrDataUrl(null);
  };

  const dismissCodes = () => {
    totpDialogs.current.codes = false;
    setShownCodes(null);
    clearPendingTotpRecoveryCodes(queryClient);
    emitHold();
  };

  const handleCopyCodes = async () => {
    if (!visibleCodes) return;
    try {
      await navigator.clipboard.writeText(visibleCodes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const rearmRejectionNotice = () => {
    setRejectionNoticeLatched(false);
    setDismissedNoticeShown(false);
  };

  const submitTotpCode = (trimmed: string) => {
    if (otherPending) return;
    rearmRejectionNotice();
    void stepUp.run(
      (proof) => {
        const cached = queryClient.getQueryData<PasskeyListResponse>(passkeysKeys.list());
        return verify
          .mutateAsync({
            code: trimmed,
            stepUp: proof,
            replacesRecoveryCodes: totpConfirmationReplacesCodes(
              cached ?? data,
              cached?.mfaEnabled ?? mfaEnabled,
            ),
          })
          .then((result) => {
            if (readDismissedConfirmation(queryClient) === "totp") {
              clearDismissedConfirmation(queryClient);
            }
            if (result.recoveryCodes) {
              totpDialogs.current.codes = true;
              setShownCodes(result.recoveryCodes);
              emitHold();
            }
            finishSetup();
          });
      },
      { needsProof: mfaEnabled, dashboardOnly: true, onDismiss: verify.reset },
    );
  };

  const handleDeleteTotp = () => {
    if (otherPending) return;
    setDeleteOpen(false);
    void stepUp.run(
      async (proof) => {
        await remove.mutateAsync(proof ? { stepUp: proof } : undefined);
        setRemoval("settled");
        if (readDismissedConfirmation(queryClient) === "totp") {
          clearDismissedConfirmation(queryClient);
        }
      },
      { onDismiss: remove.reset },
    );
  };

  const mutationError = [setup.error, remove.error].find(
    (err): err is ApiError =>
      err instanceof ApiError && err.status !== 503 && err.code !== "step_up_required",
  );
  const staleSession = isStaleSessionError(mutationError);

  return (
    <>
      {showCheckFailed ? (
        <FeatureCheckFailedCard
          icon={Smartphone}
          title={t("account.totp.title")}
          message={
            listSessionEnded ? t("account.totp.sessionEnded") : t("account.totp.checkFailed")
          }
          retrying={checkRetrying || isFetching || isPaused}
          retryFailed={checkRetryFailed}
          waitingForNetwork={waitingForNetwork}
          announceWaiting={checkRetrying && waitingForNetwork}
          sessionEnded={listSessionEnded}
          restoreFocusRef={restoreHeadingRef}
          onRetry={
            carrierOwnsRetry
              ? undefined
              : () => {
                  setCheckRetrying(true);
                  void refetch().then((result) => {
                    setCheckRetrying(false);
                    setCheckRetryFailed(result.isError);
                    if (result.isError) restoreHeadingRef.current = false;
                  });
                }
          }
        />
      ) : (
        <div className="rounded-xl border border-border bg-surface p-6">
          <div className="flex items-center justify-between">
            <h3
              ref={headingRef}
              tabIndex={-1}
              className="flex items-center gap-2 text-base font-semibold text-heading"
            >
              <Smartphone size={18} />
              {t("account.totp.title")}
            </h3>
            {totpEnabled ? (
              <button
                type="button"
                onClick={() => {
                  if (removeBusy || otherPending) return;
                  setDeleteOverdue(
                    data?.mfaSetupDeadline != null && data.mfaSetupDeadline < Date.now(),
                  );
                  setRemoveCarriesEarlier(rejectionOutstanding || dismissedConfirmation !== null);
                  if (dismissedConfirmation !== null) clearDismissedConfirmation(queryClient);
                  if (stepUp.dismissedRequired || cacheProvenRejection) {
                    stepUp.acknowledgeDismissed();
                  }
                  consumeProvenTotpStepUpRejections(queryClient);
                  rearmRejectionNotice();
                  setDeleteOpen(true);
                }}
                aria-disabled={removeBusy || otherPending || undefined}
                aria-busy={removeBusy || undefined}
                aria-describedby={
                  removePaused
                    ? removePausedId
                    : !removeBusy && otherPending
                      ? otherPendingId
                      : undefined
                }
                className="rounded-lg border border-danger/30 bg-danger/10 px-3.5 py-2 text-xs font-medium text-danger transition-colors hover:bg-danger/20 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
              >
                {removeBusy ? t("account.totp.removing") : t("account.totp.remove")}
              </button>
            ) : (
              <button
                type="button"
                onClick={startSetup}
                disabled={data === undefined || staleSession}
                aria-disabled={setupLocked || undefined}
                aria-busy={setupStarting || undefined}
                aria-describedby={
                  setupUnavailable ? unavailableId : setupPaused ? pausedId : undefined
                }
                className="rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
              >
                {setupStarting ? t("account.totp.starting") : t("account.totp.add")}
              </button>
            )}
          </div>
          <p aria-live="polite" aria-atomic="true" className="sr-only">
            {removal === "landed" ? t("account.totp.removed") : ""}
          </p>
          <p className="mt-1 text-xs text-muted">
            {totpEnabled ? t("account.totp.enabledSubtitle") : t("account.totp.subtitle")}
          </p>

          <ApiErrorBanner error={error} className="mt-4" />
          {setupUnavailable && (
            <p id={unavailableId} role="alert" className="mt-3 text-xs text-muted">
              {t("account.totp.unavailable")}
            </p>
          )}
          {setupPaused && (
            <p id={pausedId} role="alert" className="mt-3 text-xs text-muted">
              {t("common.waitingForNetwork")}
            </p>
          )}
          {setupUnanswered && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {t("common.error")}
            </p>
          )}
          {removePaused && (
            <p id={removePausedId} role="alert" className="mt-3 text-xs text-muted">
              {t("common.waitingForNetwork")}
            </p>
          )}
          {removeUnanswered && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {t("common.writeTimeout")}
            </p>
          )}
          {mutationError && !staleSession && (
            <p role="alert" className="mt-3 text-xs text-danger">
              {mutationError.error ?? t("common.error")}
            </p>
          )}
          {staleSession && <StaleSessionNotice returnTo="/account" className="mt-4" autoFocus />}
          {showDismissedNotice && (
            <p
              id={namedDismissal ? dismissedConfirmationNoticeId : undefined}
              role="alert"
              className="mt-3 text-xs text-danger"
            >
              {t("account.stepUp.dismissedIncomplete")}
            </p>
          )}
          {showCachedNotice && (
            <p id={dismissedConfirmationNoticeId} role="alert" className="mt-3 text-xs text-danger">
              {t("account.stepUp.dismissedIncomplete")}
            </p>
          )}
          {otherPendingLabel && !setupOpen && (
            <p id={otherPendingId} className="sr-only">
              {otherPendingLabel}
            </p>
          )}
        </div>
      )}

      <Dialog open={setupOpen} onClose={closeSetup} title={t("account.totp.setupTitle")}>
        <p className="text-sm text-muted">{t("account.totp.setupHint")}</p>

        {qrDataUrl && (
          <div className="mt-4 flex justify-center">
            <img
              src={qrDataUrl}
              alt={t("account.totp.qrAlt")}
              className="rounded-lg border border-border bg-white p-2"
            />
          </div>
        )}

        {setup.data?.secret && (
          <p className="mt-3 text-center font-mono text-xs tracking-wider text-muted">
            {setup.data.secret}
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (verify.isPending || otherPending) return;
            const trimmed = code.trim();
            if (!trimmed) return;
            submitTotpCode(trimmed);
          }}
          className="mt-4 flex flex-col gap-3"
        >
          <input
            id="totp-setup-code"
            name="otp"
            type="text"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="123456"
            aria-label={t("account.totp.codeLabel")}
            autoComplete="one-time-code"
            maxLength={7}
            readOnly={verify.isPending}
            aria-disabled={verify.isPending || undefined}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-center font-mono text-sm tracking-widest text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo disabled:opacity-50 aria-disabled:opacity-50 hover:border-border-light"
          />
          {isStaleSessionError(verify.error) ? (
            <StaleSessionNotice returnTo="/account" autoFocus />
          ) : verify.error && verify.error.code !== "step_up_required" ? (
            <p role="alert" className="text-xs text-danger">
              {verify.error.error ?? t("common.error")}
            </p>
          ) : stepUp.dismissedRequired || dismissedConfirmation === "totp" ? (
            <p role="alert" className="text-xs text-danger">
              {t("account.stepUp.dismissedIncomplete")}
            </p>
          ) : null}
          {setupShowsCarried && (
            <p role="alert" className="text-xs text-danger">
              {t("account.stepUp.earlierActionIncomplete")}
            </p>
          )}
          {otherPendingLabel && setupOpen && (
            <p id={otherPendingId} className="sr-only">
              {otherPendingLabel}
            </p>
          )}
          <button
            type="submit"
            disabled={
              !verify.isPending &&
              !otherPending &&
              (!code.trim() || isStaleSessionError(verify.error))
            }
            aria-disabled={verify.isPending || otherPending || undefined}
            aria-describedby={!verify.isPending && otherPending ? otherPendingId : undefined}
            aria-busy={verify.isPending || undefined}
            data-autofocus={verify.isPending ? true : undefined}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {verify.isPending ? t("account.totp.verifying") : t("account.totp.confirm")}
          </button>
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        closeOnConfirm={false}
        onConfirm={handleDeleteTotp}
        title={t("account.totp.remove")}
        description={
          (removeCarriesEarlier ? `${t("account.stepUp.earlierActionIncomplete")} ` : "") +
          (!hasPasskey && deleteOverdue
            ? t("account.totp.removeLastFactorOverdueConfirm")
            : t("account.totp.removeConfirm"))
        }
        confirmLabel={t("account.totp.remove")}
        cancelLabel={t("common.cancel")}
        destructive
        isPending={remove.isPending}
      />

      <Dialog
        open={visibleCodes !== null}
        onClose={dismissCodes}
        title={t("account.passkeys.recoveryCodesTitle")}
      >
        <p className="text-sm text-muted">{t("account.passkeys.recoveryCodesHint")}</p>
        <ul className="mt-4 grid grid-cols-1 gap-2 rounded-lg bg-elevated/50 p-4 font-mono text-sm text-heading sm:grid-cols-2">
          {(visibleCodes ?? []).map((recoveryCode) => (
            <li key={recoveryCode}>{recoveryCode}</li>
          ))}
        </ul>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleCopyCodes}
            data-autofocus
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? t("account.passkeys.copied") : t("account.passkeys.copy")}
          </button>
          <button
            type="button"
            onClick={dismissCodes}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
          >
            {t("account.passkeys.codesSaved")}
          </button>
        </div>
      </Dialog>

      <StepUpDialog
        key={stepUp.nonce}
        open={stepUp.open}
        busy={stepUp.busy}
        error={stepUp.error}
        proofOnWire={stepUp.proofOnWire}
        actionNeedsDashboardSession={stepUp.dashboardOnly}
        supportsWebAuthn={browserSupportsWebAuthn()}
        hasPasskey={hasPasskey || passkeyImpliedByServer}
        hasTotp={totpEnabled}
        onPasskey={stepUp.withPasskey}
        onTotp={stepUp.withTotp}
        onCancel={stepUp.cancel}
        ceremonyOwner={ceremonyOwner}
        subtitle={t("account.stepUp.subtitle")}
        staleSessionBody={t("account.staleSession.body")}
        returnTo="/account"
      />
    </>
  );
}
